import * as React from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import { ITagPlate } from '@models';

import Subjects from './Subjects';
import Flowcells, { ITagWellsTagWell } from './TagWells';
import { ISubjectStateProps } from '../../../components/upload/TagPlate/constants';
import { ReactComponent as PlateIcon } from '@assets/images/plate-icon.svg';

import './TagPlate.scss';

export interface ITagPlateStateProps extends ITagPlate {
  newExperiment: boolean;
}

const TagPlate: React.FC<ITagPlateStateProps> = ({
  newExperiment,
  samples: tagPlateSubjects,
  plateLayout: name,
}) => {
  const { t } = useTranslation();
  const [wells, setWells] = React.useState<ITagWellsTagWell[]>([]);
  const [subjects, setSubjects] = React.useState<ISubjectStateProps[]>([]);
  const [selectedExternalSubjectId, setSelectedExternalSubjectId] =
    React.useState('');

  const [selectedEmptyTagWell, setSelectedEmptyTagWell] = React.useState('');

  React.useEffect(() => {
    const initWells: ITagWellsTagWell[] = _(tagPlateSubjects)
      .map((x) =>
        x.wells.map((y) => ({
          displayColor: x.displayColor,
          selected: false,
          string: y.string,
          controlType: x.controlType,
          externalSubjectId: x.externalSubjectId,
        }))
      )
      .flatten()
      .value();
    setWells(initWells);

    const initSubjects: ISubjectStateProps[] = tagPlateSubjects.map((x) => ({
      externalSubjectId: x.externalSubjectId,
      wells: x.wells.map((y) => y.string),
      properties: x.properties,
      selected: false,
      displayColor: x.displayColor,
      name: x.name,
      testType: x.testType,
      controlType: x.controlType,
    }));
    setSubjects(initSubjects);
  }, [tagPlateSubjects]);

  const selectWellsAndRows = (externalSubjectId: string) => {
    if (externalSubjectId === selectedExternalSubjectId) {
      externalSubjectId = '';
    }

    const newWells = wells.slice();
    newWells.forEach(
      (x) => (x.selected = externalSubjectId === x.externalSubjectId)
    );
    setWells(newWells);

    const newSubjects = subjects.slice();
    newSubjects.forEach(
      (x) => (x.selected = externalSubjectId === x.externalSubjectId)
    );
    setSubjects(newSubjects);

    setSelectedExternalSubjectId(externalSubjectId);
    setSelectedEmptyTagWell('');
  };

  const selectEmptyTagWell = (position: string) => {
    if (position === selectedEmptyTagWell) {
      position = '';
    }
    selectWellsAndRows('');
    setSelectedEmptyTagWell(position);
  };

  return (
    <div className='tag-plate px-3 pb-3 my-3'>
      <PlateIcon className='plate-icon' />
      <span className='font-size-13 font-weight-bold align-middle'>
        {t('upload_tagPlate')}: {name}
      </span>
      <div className='d-flex mt-2'>
        <div>
          <Flowcells
            tagWells={wells}
            select={(well: ITagWellsTagWell) =>
              selectWellsAndRows(well.externalSubjectId)
            }
            selectEmptyTagWell={selectEmptyTagWell}
            selectedEmptyTagWell={selectedEmptyTagWell}
          />
        </div>
        <div className='flex-grow-1 ml-2'>
          <Subjects
            newExperiment={newExperiment}
            subjects={subjects}
            select={(subject: ISubjectStateProps) =>
              selectWellsAndRows(subject.externalSubjectId)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default TagPlate;
